import { getIn, setIn } from "formik";

import { isPlainObject } from "./common";

// Search operators as they read in a sentence; anything missing reads as
// "Label: value".
const OPERATOR_PHRASES = {
  between: "between",
  gt: ">",
  gte: "≥",
  isNotNull: "is not empty",
  isNull: "is empty",
  like: "contains",
  lt: "<",
  lte: "≤",
  ne: "is not",
  notIn: "is not",
  notLike: "does not contain",
};

const getSearchSynopsisOperatorPhrase = operator => OPERATOR_PHRASES[operator];

const formatSearchSynopsisValues = values =>
  (values || []).map(value => value.label).join(" / ");

const formatSearchSynopsisTerm = term => {
  const phrase = getSearchSynopsisOperatorPhrase(term?.operator);
  const values = formatSearchSynopsisValues(term?.values);
  if (!values) return phrase ? `${term.label} ${phrase}` : term?.label;
  return phrase
    ? `${term.label} ${phrase} ${values}`
    : `${term.label} ${values}`;
};

const formatSearchSynopsis = terms =>
  terms?.length > 0
    ? terms.map(formatSearchSynopsisTerm).join(", ")
    : undefined;

/**
 * What a tag's remove button takes out of the search: a single value when
 * the term lists several removable values, otherwise the whole field (or the
 * value's own path, e.g. one sort slot).
 */
const getSearchSynopsisRemoval = (term, value) => {
  if (value?.path) return { path: value.path };
  const isOneOfSeveral =
    term.values.length > 1 &&
    value?.value != null &&
    !term.values.some(item => item.value == null);
  return isOneOfSeveral
    ? { path: term.path, value: value.value }
    : { path: term.path };
};

const optionValue = item =>
  isPlainObject(item) && "value" in item ? item.value : item;

const withoutListValue = (list, value) =>
  list.filter(item => String(optionValue(item)) !== value);

/**
 * Pure: take one search term (or one of its values) out of a search, whether
 * that is the backend filter (Contacts' URL) or the raw form values. Removing
 * a whole field restores its default, or drops it when there is none, so
 * operator-only defaults like `{ operator: "in" }` survive for the form.
 */
const removeSearchSynopsisTerm = (search, { path, value }, defaults) => {
  const resetField = () => setIn(search, path, getIn(defaults, path));
  const current = getIn(search, path);
  if (value === undefined) return resetField();

  if (Array.isArray(current)) {
    const remaining = withoutListValue(current, value);
    return remaining.length > 0 ? setIn(search, path, remaining) : resetField();
  }

  if (isPlainObject(current) && Array.isArray(current.value)) {
    const remaining = withoutListValue(current.value, value);
    return remaining.length > 0
      ? setIn(search, path, { ...current, value: remaining })
      : resetField();
  }

  return resetField();
};

export {
  formatSearchSynopsis,
  formatSearchSynopsisTerm,
  getSearchSynopsisOperatorPhrase,
  getSearchSynopsisRemoval,
  removeSearchSynopsisTerm,
};
